import type { JSX, ReactNode } from 'react';
import { matchListItem, parseList, type MdListItem } from './markdown-list.ts';

/**
 * A deliberately small Markdown subset: fenced code, headings, lists, quotes,
 * and inline code/bold/italic/links.
 *
 * Agents answer in Markdown and the interesting part is almost always a code
 * block or a file path, so those get proper treatment (horizontal scroll, a
 * copy button) and everything exotic degrades to plain text. Output is React
 * nodes rather than HTML, so there is no innerHTML anywhere in the app.
 */
export function Markdown({ text }: { text: string }): JSX.Element {
  return <>{renderBlocks(text)}</>;
}

function renderBlocks(text: string): ReactNode[] {
  const lines = text.split('\n');
  const out: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';

    const fence = /^\s*```(\S*)\s*$/.exec(line);
    if (fence) {
      const lang = fence[1] ?? '';
      const body: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i] ?? '')) body.push(lines[i] ?? ''), i++;
      i++; // closing fence
      out.push(<CodeBlock key={key++} lang={lang} code={body.join('\n')} />);
      continue;
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      const level = Math.min(4, heading[1]!.length);
      const Tag = (['h3', 'h4', 'h5', 'h6'] as const)[level - 1]!;
      out.push(<Tag key={key++} className="md-h">{inline(heading[2] ?? '')}</Tag>);
      i++;
      continue;
    }

    if (matchListItem(line)) {
      const parsed = parseList(lines, i);
      const rendered = renderListTree(parsed.items, key);
      out.push(...rendered.nodes);
      key = rendered.key;
      i = parsed.next;
      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      const quoted: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i] ?? '')) {
        quoted.push((lines[i] ?? '').replace(/^\s*>\s?/, ''));
        i++;
      }
      out.push(<blockquote key={key++} className="md-quote">{inline(quoted.join('\n'))}</blockquote>);
      continue;
    }

    if (!line.trim()) {
      i++;
      continue;
    }

    const para: string[] = [];
    while (
      i < lines.length &&
      (lines[i] ?? '').trim() &&
      !/^\s*```/.test(lines[i] ?? '') &&
      !/^(#{1,4})\s/.test(lines[i] ?? '') &&
      !matchListItem(lines[i] ?? '') &&
      !/^\s*>\s?/.test(lines[i] ?? '')
    ) {
      para.push(lines[i] ?? '');
      i++;
    }
    out.push(<p key={key++} className="md-p">{inline(para.join('\n'))}</p>);
  }

  return out;
}

function renderListTree(
  items: MdListItem[],
  key: number,
): { nodes: ReactNode[]; key: number } {
  const nodes: ReactNode[] = [];
  let i = 0;
  while (i < items.length) {
    const ordered = items[i]!.ordered;
    const group: MdListItem[] = [];
    while (i < items.length && items[i]!.ordered === ordered) {
      group.push(items[i]!);
      i++;
    }
    const Tag = ordered ? 'ol' : 'ul';
    nodes.push(
      <Tag key={key++} className="md-list">
        {group.map((item, idx) => (
          <li key={idx}>
            {inline(item.content)}
            {item.children.length > 0 ? renderListTree(item.children, 0).nodes : null}
          </li>
        ))}
      </Tag>,
    );
  }
  return { nodes, key };
}

const INLINE = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*\n]+\*)|(\[[^\]]+\]\([^)\s]+\))/g;

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const match of text.matchAll(INLINE)) {
    const idx = match.index;
    if (idx > last) out.push(text.slice(last, idx));
    const token = match[0];
    if (token.startsWith('`')) {
      out.push(<code key={key++} className="md-code">{token.slice(1, -1)}</code>);
    } else if (token.startsWith('**')) {
      out.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('*')) {
      out.push(<em key={key++}>{token.slice(1, -1)}</em>);
    } else {
      const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token)!;
      out.push(
        <a key={key++} href={link[2]} target="_blank" rel="noreferrer noopener">
          {link[1]}
        </a>,
      );
    }
    last = idx + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function CodeBlock({ lang, code }: { lang: string; code: string }): JSX.Element {
  return (
    <div className="md-pre-wrap">
      <div className="md-pre-bar">
        <span>{lang || 'code'}</span>
        <button
          type="button"
          onClick={() => void navigator.clipboard?.writeText(code)}
          aria-label="Copy code"
        >
          copy
        </button>
      </div>
      <pre className="md-pre">
        <code>{code}</code>
      </pre>
    </div>
  );
}
